import {
  FC, useEffect, useRef, useState,
} from 'react';
import {
  Button, Card, Row, Typography, Input, message, Spin, Col, Dropdown,
} from 'taxaroo-ui';
import AiOpen from 'src/assets/icons/ai-open.svg';
import AiIcon from 'src/assets/icons/ai-icon.svg';
import Mic from 'src/assets/icons/mic.svg';
import Send from 'src/assets/icons/send.svg';
import { useAppDispatch, useAppSelector } from '~src/redux/hooks';
import Switch from 'react-switch';
import StreamingAvatar, { StreamingEvents, TaskMode, TaskType } from '@heygen/streaming-avatar';
import { isMobileView } from '~src/components/helpers/screen';
import { setGreetingsDone, setHeygenOpen } from '~src/redux/slices/aiMessagesSlice';
import QuotesIcon from 'src/assets/icons/quotes.svg';
import { GetAiAgentMessagesDocument, useGetAiAgentMessagesLazyQuery } from '~src/graphql/queries/messaging';
import { useCreateAiAgentMessageMutation } from '~src/graphql/mutations/messaging';
import { ApolloError } from '@apollo/client';
import { AiAgentMessageEntity, TaxarooAiMessageType } from '~src/graphql';
import * as styles from './styles.module.css';
import {
  connectToServer, FirmAiPlanType, workletString, wsSendAudioData,
} from './utils';
import HeygenAvatar, { HeygenAvatarProps } from './Avatar';
import AddonsModal from './AddonsModal';
import AiMessageList from './Messages';

const { Text } = Typography;

export type AiAssistantPlanType = 'AI Chat' | 'AI VideoPlus' | 'AI VideoPro';

type AiAssistantProps = {
  isLeftSideMenuCollapsed: boolean;
};

let ws: WebSocket;
let mediaStream: MediaStream;
let audioContext: AudioContext;
let buffer = new Uint8Array();

const AiAssistant:FC<AiAssistantProps> = ({ isLeftSideMenuCollapsed }) => {
  const { accessToken } = useAppSelector((state) => state.session);
  const { heygenStateOpen, isGreetingsDone } = useAppSelector((state) => state.aiMessages);
  const dispatch = useAppDispatch();

  const [getAiAgentMessages, { data: aiAgentMessages, loading: aiAgentMessagesLoading }] = useGetAiAgentMessagesLazyQuery();
  const [createAiAgentMessage] = useCreateAiAgentMessageMutation({
    refetchQueries: [GetAiAgentMessagesDocument],
    awaitRefetchQueries: true,
    onError: (err: ApolloError) => message.error(err.message),
  });

  const [isOpen, setIsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isRecordingLoading, setIsRecordingLoading] = useState(false);
  const [isWsConnected, setIsWsConnected] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<Array<AiAgentMessageEntity>>([]);
  const [isHeygenOpen, setIsHeygenOpen] = useState(heygenStateOpen);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [isHeygenLoading, setIsHeygenLoading] = useState(false);
  const [heygenParams, setHeygenParams] = useState<HeygenAvatarProps>();
  const [heygenSessionId, setHeygenSessionId] = useState<string>();
  const [isFullScreenAvatar, setIsFullScreenAvatar] = useState(false);
  const [isTrialUsageLimitExceeded, setIsTrialUsageLimitExceeded] = useState(false);
  const [isHeygenUsageLimitExceeded, setIsHeygenUsageLimitExceeded] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<FirmAiPlanType>('trial');
  const [isPromptsVisible, setIsPromptsVisible] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>();
  const avatar = useRef<StreamingAvatar>();
  const isAvatarSpeaking = useRef<boolean>(false);
  const promptIdeas = [
    'Show me what you can do.',
    'Can you help me fill out Form 1040?',
    'Draft an email to my accountant asking for missing documents.',
    'What are the tax implications of selling my house?',
  ];

  const closeHeygen = (sessionId: string) => {
    if (ws?.readyState === WebSocket.OPEN && sessionId) {
      ws?.send(JSON.stringify({ event: 'heygen_stop', data: sessionId }));
    }
    setIsHeygenLoading(false);
    setHeygenParams({
      token: undefined, avatarId: undefined, voiceId: undefined, secondsLeft: 0,
    });
    setHeygenSessionId(undefined);
    setIsFullScreenAvatar(false);
    isAvatarSpeaking.current = false;
    avatar.current = null;
  };

  const setSteamingAvatar = (val: StreamingAvatar, sessionId: string) => {
    avatar.current = val;
    setHeygenSessionId(sessionId);

    ws?.send(JSON.stringify({
      event: 'heygen_session_id',
      data: sessionId,
    }));

    if (!isGreetingsDone) {
      avatar.current?.speak({
        text: `
          Welcome to Taxaroo AI!
          You can chat here or talk to Taxaroo AI with your microphone
          to get your questions answered and complete your tasks.
          How can I assist you today?
        `,
        task_type: TaskType.REPEAT,
        taskMode: TaskMode.SYNC,
      });
      dispatch(setGreetingsDone({ isGreetingsDone: true }));
    }

    avatar.current?.on(StreamingEvents.STREAM_DISCONNECTED, () => closeHeygen(sessionId));
    avatar.current?.on(StreamingEvents.AVATAR_START_TALKING, () => { isAvatarSpeaking.current = true; });
    avatar.current?.on(StreamingEvents.AVATAR_STOP_TALKING, () => { isAvatarSpeaking.current = false; });
  };

  const stopRecording = async () => {
    if (audioContext?.state === 'running') {
      await audioContext.close();
    }
    mediaStream?.getTracks().forEach((track) => track.stop());
    setIsRecording(false);
    setIsRecordingLoading(false);
  };

  const heygenStreamStart = () => {
    setIsHeygenLoading(true);
    ws?.send(JSON.stringify({ event: 'heygen_start' }));
  };

  const scrollToBottom = () => {
    setTimeout(() => messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 200);
  };

  const onConnectionReady = (planType: FirmAiPlanType) => {
    if (typeof isHeygenOpen === 'undefined') {
      setIsHeygenOpen(true);
    }

    setCurrentPlan(planType);
    setIsWsConnected(true);
    scrollToBottom();
  };

  const onConnectionError = async () => {
    await stopRecording();
    closeHeygen(heygenSessionId);
    setIsWsConnected(false);
    message.error('Connection error. Please try again later.', 10);
    ws?.close();
  };

  const onMessage = async (msg: AiAgentMessageEntity) => {
    setMessages((msgs) => [...msgs, msg]);
    scrollToBottom();
    // console.log('onMessage', msg, avatar.current);
    if (msg.type === TaxarooAiMessageType.Assistant && avatar.current && !isAvatarSpeaking.current) {
      await avatar.current.speak({ text: msg.text, task_type: TaskType.REPEAT, taskMode: TaskMode.SYNC });
    }
    // console.log('onMessage', msg.type, isAvatarSpeaking.current);
    if (msg.type === TaxarooAiMessageType.User && isAvatarSpeaking.current) {
      await avatar.current?.interrupt();
    }
  };

  const onProcessing = (isProcessing: boolean) => {
    setAiProcessing(isProcessing);
    scrollToBottom();
  };

  const onHeygenStart = (token: string, avatarId: string, voiceId: string, secondsLeft: number) => {
    setHeygenParams({
      token, avatarId, voiceId, secondsLeft,
    });
    setIsHeygenLoading(false);
  };

  const onHeygenStop = () => {
    setIsHeygenLoading(false);
  };

  const onError = async (msg: string) => {
    if (msg === 'AI trial usage limit exceeded') {
      await avatar.current?.stopAvatar();
      setIsTrialUsageLimitExceeded(true);
      setIsHeygenLoading(false);
    } else if (msg === 'Video avatar usage limit exceeded') {
      await avatar.current?.stopAvatar();
      setIsHeygenUsageLimitExceeded(true);
      setIsHeygenLoading(false);
    } else {
      message.error(msg, 10);
    }
  };

  const toggleRecording = async () => {
    try {
      if (isRecording) {
        await stopRecording();
      } else {
        setIsRecordingLoading(true);
        // Request Microphone Access
        mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioContext = new AudioContext({ sampleRate: 24000 });
        await audioContext.audioWorklet.addModule(
          URL.createObjectURL(new Blob([workletString], { type: 'application/javascript' })),
        );
        const mediaStreamSource = audioContext.createMediaStreamSource(mediaStream);
        const workletNode = new AudioWorkletNode(audioContext, 'audio-processor-worklet');
        workletNode.port.onmessage = (event) => {
          buffer = wsSendAudioData(buffer, event.data.buffer, ws);
        };
        mediaStreamSource.connect(workletNode);
        setIsRecording(true);
        setIsRecordingLoading(false);
      }
    } catch (err) {
      setIsRecordingLoading(false);
      message.error(err.message, 10);
    }
  };

  const sendUserMessage = (text = '') => {
    const val = text || inputValue.trim();
    if (!val) return;

    if (ws.readyState !== WebSocket.OPEN) {
      message.error('Connection to server was closed. Please close and then open Taxaroo AI.', 10);
      return;
    }

    if (!text) {
      setInputValue('');
    }

    ws.send(JSON.stringify({
      event: 'text',
      data: val,
    }));
  };

  const getElementCssClass = (name: 'card' | 'avatar' | 'msg') => {
    if (name === 'card') {
      if (isMobileView()) {
        return isHeygenOpen ? styles.cardMobileWithHeygen : styles.cardMobile;
      }
      return isLeftSideMenuCollapsed ? styles.cardCollapsed : styles.card;
    }

    if (name === 'avatar') {
      if (isMobileView()) {
        return isFullScreenAvatar ? styles.heygenMobileFullScreen : styles.heygenMobile;
      }
      return isLeftSideMenuCollapsed ? styles.heygenContainerCollapsed : styles.heygenContainer;
    }

    if (name === 'msg') {
      if (isMobileView()) {
        return isHeygenOpen ? styles.messagesMobileWithHeygen : styles.messagesMobile;
      }
      return styles.messages;
    }

    return '';
  };

  const connect = () => {
    ws = connectToServer(
      accessToken,
      (planType: FirmAiPlanType) => onConnectionReady(planType),
      () => onConnectionError(),
      async (msg: AiAgentMessageEntity) => onMessage(msg),
      (isProcessing: boolean) => onProcessing(isProcessing),
      (token: string, avatarId: string, voiceId: string, secondsLeft: number) => onHeygenStart(token, avatarId, voiceId, secondsLeft),
      () => onHeygenStop(),
      (msg: string) => onError(msg),
      () => { setIsWsConnected(false); closeHeygen(heygenSessionId); },
    );
  };

  const subscriptionCreated = async (plan: AiAssistantPlanType) => {
    setIsTrialUsageLimitExceeded(false);
    setIsHeygenUsageLimitExceeded(false);
    setIsModalVisible(false);

    await createAiAgentMessage({
      variables: {
        createAiAgentMessageInput: {
          type: TaxarooAiMessageType.System,
          text: `Welcome back! '${plan}' plan unlocked`,
        },
      },
    });

    connect();
  };

  const subscriptionCancelled = () => {
    setIsModalVisible(false);
  };

  useEffect(() => {
    if (isOpen) {
      connect();
    } else {
      stopRecording();
      setAiProcessing(false);
      setIsTrialUsageLimitExceeded(false);
      setIsHeygenUsageLimitExceeded(false);
      setIsPromptsVisible(false);
      ws?.close();

      if (isHeygenOpen) {
        setIsHeygenOpen(undefined);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!aiAgentMessagesLoading && aiAgentMessages?.getAiAgentMessages?.data) {
      const allMessages = messages
        .slice()
        .filter((msg) => msg.type !== TaxarooAiMessageType.Special);

      aiAgentMessages?.getAiAgentMessages?.data?.forEach((msg) => {
        if (!messages.some((savedMsg) => savedMsg.id === msg.id)) {
          allMessages.push(msg);
        }
      });

      allMessages.sort((a, b) => (a.createAt < b.createAt ? -1 : 1));

      setMessages([
        {
          type: TaxarooAiMessageType.Special,
          more: 'terms',
        } as AiAgentMessageEntity,
        ...allMessages,
      ]);
    }
  }, [aiAgentMessages, aiAgentMessagesLoading]);

  useEffect(() => {
    getAiAgentMessages();
  }, []);

  return (
    <>
      <Card
        className={getElementCssClass('card')}
        title={(
          <Row justify="space-between">
            <Row justify="start" align="middle">
              <Row className={styles.titleIconContainer}>
                <AiOpen />
              </Row>
              <Text>Taxaroo AI</Text>
            </Row>
            <Row justify="start" align="middle">
              {!isMobileView() && (
                <Switch
                  className={styles.aiSwitch}
                  offColor="#d9d9d9"
                  onColor="#3d80f3"
                  height={32}
                  onChange={(val) => {
                    setIsHeygenOpen(val);
                    dispatch(setHeygenOpen({ heygenStateOpen: val }));
                    if (!val) closeHeygen(heygenSessionId);
                  }}
                  checked={isHeygenOpen || false}
                  uncheckedHandleIcon={<AiIcon className={styles.aiSwitchIcon} />}
                  checkedHandleIcon={<AiIcon className={styles.aiSwitchIcon} />}
                  checkedIcon={false}
                  uncheckedIcon={false}
                />
              )}
            </Row>
          </Row>
        )}
        extra={<Text className={styles.close} onClick={() => setIsOpen(!isOpen)}>✕</Text>}
        style={isOpen ? { display: 'block' } : { display: 'none' }}
        headStyle={{ borderBottom: '1px solid #eee' }}
        bodyStyle={{ padding: 0 }}
      >
        {isWsConnected || isTrialUsageLimitExceeded
          ? (
            <div style={{ position: 'relative' }}>
              <div
                className={getElementCssClass('msg')}
                style={{ filter: isTrialUsageLimitExceeded ? 'blur(3px)' : 'none' }}
              >
                <AiMessageList
                  messages={messages}
                  aiAgentMessagesLoading={aiAgentMessagesLoading}
                  hasMore={aiAgentMessages?.getAiAgentMessages?.pagination?.total > messages.length - 1}
                  loadMore={async () => {
                    if (aiAgentMessages?.getAiAgentMessages?.pagination?.skip >= 0) {
                      await getAiAgentMessages({
                        variables: {
                          skip: aiAgentMessages.getAiAgentMessages.pagination.skip + 10,
                        },
                      });
                    }
                  }}
                />

                <Row justify="center" align="middle">
                  {aiProcessing && (<Spin />)}
                </Row>
                <div ref={messagesEndRef} />
              </div>

              <Row justify="space-between" align="middle" className={styles.controls}>
                <Input
                  value={inputValue}
                  placeholder="Send Message..."
                  className={styles.input}
                  onChange={(evt: any) => setInputValue(evt.target.value)}
                  onPressEnter={() => sendUserMessage()}
                  disabled={isTrialUsageLimitExceeded}
                  suffix={(
                    <Button
                      type="link"
                      block
                      className={styles.micBtn}
                      loading={isRecordingLoading}
                      onClick={() => toggleRecording()}
                      disabled={isTrialUsageLimitExceeded}
                    >
                      {!isRecordingLoading && (<Mic className={isRecording ? styles.micRec : styles.micDef} />)}
                    </Button>
                  )}
                />

                <Dropdown
                  placement="topRight"
                  trigger={['click']}
                  disabled={isTrialUsageLimitExceeded}
                  visible={isPromptsVisible}
                  overlay={(
                    <Card
                      title="Prompts ideas"
                      className={styles.promptIdeasCard}
                      headStyle={{ padding: '0 20px' }}
                      bodyStyle={{ padding: 0 }}
                      extra={<Text className={styles.close} onClick={() => setIsPromptsVisible(false)}>✕</Text>}
                    >
                      {promptIdeas.map((text, i) => (
                        <Row
                          key={i}
                          className={styles.promptIdeasRow}
                          align="middle"
                          onClick={() => { setIsPromptsVisible(false); sendUserMessage(text); }}
                        >
                          <Col span={22}>
                            &quot;
                            {text}
                            &quot;
                          </Col>
                          <Col span={2}><Send className={styles.promptIdeasIcon} /></Col>
                        </Row>
                      ))}
                    </Card>
                  )}
                >
                  <QuotesIcon
                    className={styles.quoteIcon}
                    onClick={() => setIsPromptsVisible(!isPromptsVisible)}
                  />
                </Dropdown>

                <Button
                  className={styles.sendBtn}
                  type="primary"
                  shape="circle"
                  onClick={() => sendUserMessage()}
                  disabled={isTrialUsageLimitExceeded}
                >
                  <Send className={styles.sendIcon} />
                </Button>
              </Row>

              {isTrialUsageLimitExceeded && (
                <Card
                  className={styles.expirationCard}
                  bodyStyle={{ padding: '10px' }}
                  onClick={() => setIsModalVisible(!isModalVisible)}
                >
                  <Row justify="middle">
                    <Col span={2} type="flex" align="middle">
                      <AiOpen className={styles.expirationCardIcon} />
                    </Col>
                    <Col span={16} style={{ paddingLeft: '20px' }}>
                      <Text className={styles.expirationCardDescription}>
                        Free trial has ended. Want to keep using Taxaroo AI
                      </Text>
                    </Col>
                    <Col span={6} type="flex" align="middle" style={{ paddingLeft: '20px' }}>
                      <Text className={styles.expirationCardLink}>
                        Get it now
                      </Text>
                    </Col>
                  </Row>
                </Card>
              )}
            </div>
          )
          : (
            <Row align="middle" justify="center" style={{ height: '470px' }}>
              <Spin size="large" />
            </Row>
          )}
      </Card>

      {isHeygenOpen && (
        <Row
          className={getElementCssClass('avatar')}
          align="middle"
          justify="center"
        >
          <HeygenAvatar
            {...heygenParams}
            setSteamingAvatar={setSteamingAvatar}
            heygenStreamStart={heygenStreamStart}
            setFullscreenAvatar={(val: boolean) => setIsFullScreenAvatar(val)}
            isTrialUsageLimitExceeded={isTrialUsageLimitExceeded}
            isHeygenUsageLimitExceeded={isHeygenUsageLimitExceeded}
            isHeygenLoading={isHeygenLoading}
            planType={currentPlan}
            isRecording={isRecording}
            isRecordingLoading={isRecordingLoading}
            toggleRecording={toggleRecording}
          />
        </Row>
      )}

      <Button
        className={isLeftSideMenuCollapsed ? styles.btnCollapsed : styles.btn}
        type="default"
        shape="round"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Row justify="space-between" align="middle">
          <AiOpen />
          {!isLeftSideMenuCollapsed && (
            <Text className={styles.btnText}>Taxaroo AI</Text>
          )}
        </Row>
      </Button>

      <AddonsModal
        isModalVisible={isModalVisible}
        closeModal={() => setIsModalVisible(false)}
        subscriptionCreated={subscriptionCreated}
        subscriptionCancelled={subscriptionCancelled}
      />
    </>
  );
};

export default AiAssistant;
